import api from './api';
import { captureEvent } from '../utils/posthog';

/**
 * Service API pour l'analyse de fusion des formations
 * @module services/analysis
 */

/**
 * Lancer une analyse de fusion entre formations
 * @param {Object} params - Paramètres d'analyse
 * @param {string} params.organizationId - ID de l'organisation
 * @param {Array<string>} params.formationIds - IDs des formations à analyser (2 minimum)
 * @returns {Promise<Object>} Résultat de l'analyse
 */
export async function runFusionAnalysis({ organizationId, formationIds }) {
  try {
    // Validation
    if (!Array.isArray(formationIds) || formationIds.length < 2) {
      return {
        success: false,
        error: 'Vous devez sélectionner au moins 2 formations pour l\'analyse'
      };
    }

    const response = await api.post('/analysis/fusion', {
      organizationId,
      formationIds
    });

    return {
      success: true,
      data: response.data.analysis,
      message: response.data.message
    };
  } catch (error) {
    console.error('Erreur analyse fusion:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de l\'analyse',
      details: error.response?.data
    };
  }
}

/**
 * Récupérer les détails d'une analyse par ID
 * @param {string} analysisId - ID UUID de l'analyse
 * @returns {Promise<Object>} Détails de l'analyse
 */
export async function getAnalysis(analysisId) {
  try {
    const response = await api.get(`/analysis/${analysisId}`);

    return {
      success: true,
      data: response.data.analysis
    };
  } catch (error) {
    console.error('Erreur récupération analyse:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Analyse introuvable',
      details: error.response?.data
    };
  }
}

/**
 * Lister les analyses d'un utilisateur
 * @param {Object} params - Paramètres de pagination
 * @param {number} [params.page=1] - Numéro de page
 * @param {number} [params.limit=10] - Nombre d'éléments par page
 * @param {string} [params.organizationId] - Filtrer par organisation
 * @returns {Promise<Object>} Liste des analyses avec pagination
 */
export async function listAnalyses({ page = 1, limit = 10, organizationId } = {}) {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    if (organizationId) params.append('organizationId', organizationId);

    const response = await api.get(`/analysis?${params.toString()}`);

    return {
      success: true,
      data: response.data.analyses,
      pagination: {
        page: response.data.page,
        limit: response.data.limit,
        total: response.data.total,
        totalPages: response.data.totalPages
      }
    };
  } catch (error) {
    console.error('Erreur liste analyses:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
      details: error.response?.data
    };
  }
}

/**
 * Calculer le niveau de similarité (low, medium, high)
 * @param {number} percentSimilarity - Pourcentage de similarité (0-100)
 * @returns {string} Niveau de similarité
 */
export function getSimilarityLevel(percentSimilarity) {
  if (percentSimilarity >= 70) return 'high';
  if (percentSimilarity >= 40) return 'medium';
  return 'low';
}

/**
 * Obtenir la couleur associée au niveau de similarité
 * @param {number} percentSimilarity - Pourcentage de similarité (0-100)
 * @returns {string} Couleur Tailwind
 */
export function getSimilarityColor(percentSimilarity) {
  const level = getSimilarityLevel(percentSimilarity);

  const colors = {
    high: 'text-green-600 bg-green-100',
    medium: 'text-yellow-600 bg-yellow-100',
    low: 'text-red-600 bg-red-100'
  };

  return colors[level] || colors.low;
}

/**
 * Obtenir le label associé au niveau de similarité
 * @param {number} percentSimilarity - Pourcentage de similarité (0-100)
 * @returns {string} Label français
 */
export function getSimilarityLabel(percentSimilarity) {
  const level = getSimilarityLevel(percentSimilarity);

  const labels = {
    high: 'Forte similarité',
    medium: 'Similarité modérée',
    low: 'Faible similarité'
  };

  return labels[level] || labels.low;
}

/**
 * Formater le temps économisé
 * @param {number} hours - Nombre d'heures
 * @returns {string} Texte formaté
 */
export function formatTimeSaved(hours) {
  if (hours === 0) return 'Aucun gain estimé';
  if (hours < 1) return `${Math.round(hours * 60)} minutes`;
  if (hours === 1) return '1 heure';

  const days = Math.floor(hours / 8); // 8h = 1 jour de travail
  const remainingHours = hours % 8;

  if (days > 0) {
    if (remainingHours > 0) {
      return `${days}j ${Math.round(remainingHours)}h`;
    }
    return `${days} jour${days > 1 ? 's' : ''}`;
  }

  return `${Math.round(hours)} heure${hours > 1 ? 's' : ''}`;
}

/**
 * Calculer le ROI de la fusion (retour sur investissement)
 * @param {number} timeSavedHours - Heures économisées
 * @param {number} [hourlyRate=50] - Taux horaire en euros
 * @returns {Object} ROI calculé
 */
export function calculateFusionROI(timeSavedHours, hourlyRate = 50) {
  const moneySaved = timeSavedHours * hourlyRate;

  return {
    timeSavedHours: Math.round(timeSavedHours * 10) / 10,
    moneySaved: Math.round(moneySaved),
    currency: '€',
    hourlyRate
  };
}

/**
 * Trier les suggestions de fusion par impact
 * @param {Array<Object>} suggestions - Liste des suggestions
 * @returns {Array<Object>} Suggestions triées par impact (high > medium > low)
 */
export function sortSuggestionsByImpact(suggestions) {
  if (!Array.isArray(suggestions)) return [];

  const impactOrder = { high: 3, medium: 2, low: 1 };

  return [...suggestions].sort((a, b) => {
    const impactA = impactOrder[a.impact] || 0;
    const impactB = impactOrder[b.impact] || 0;
    return impactB - impactA;
  });
}

/**
 * Filtrer les suggestions par niveau d'impact minimum
 * @param {Array<Object>} suggestions - Liste des suggestions
 * @param {string} minImpact - Impact minimum ('low', 'medium', 'high')
 * @returns {Array<Object>} Suggestions filtrées
 */
export function filterSuggestionsByImpact(suggestions, minImpact = 'low') {
  if (!Array.isArray(suggestions)) return [];

  const impactOrder = { low: 1, medium: 2, high: 3 };
  const minLevel = impactOrder[minImpact] || 1;

  return suggestions.filter(suggestion => {
    const level = impactOrder[suggestion.impact] || 0;
    return level >= minLevel;
  });
}

/**
 * Générer un rapport textuel d'analyse
 * @param {Object} analysis - Résultat d'analyse
 * @returns {string} Rapport formaté
 */
export function generateAnalysisReport(analysis) {
  if (!analysis) return '';

  const {
    totalUniqueCompetences,
    numSimilarCompetences,
    percentSimilarity,
    estimatedTimeSavedHours,
    suggestedMerges
  } = analysis;

  const similarityLevel = getSimilarityLabel(parseFloat(percentSimilarity));
  const timeSaved = formatTimeSaved(parseFloat(estimatedTimeSavedHours || 0));
  const roi = calculateFusionROI(parseFloat(estimatedTimeSavedHours || 0));

  let report = `📊 Analyse de fusion - ${new Date().toLocaleDateString('fr-FR')}\n\n`;
  report += `Compétences totales : ${totalUniqueCompetences}\n`;
  report += `Compétences partagées : ${numSimilarCompetences}\n`;
  report += `Similarité : ${percentSimilarity}% (${similarityLevel})\n\n`;
  report += `⏱️ Temps économisé estimé : ${timeSaved}\n`;
  report += `💰 Économie estimée : ${roi.moneySaved}€ (taux horaire: ${roi.hourlyRate}€/h)\n\n`;

  if (Array.isArray(suggestedMerges) && suggestedMerges.length > 0) {
    report += `📋 Suggestions de fusion (${suggestedMerges.length}) :\n`;
    suggestedMerges.forEach((merge, index) => {
      report += `  ${index + 1}. ${merge.reason} (Impact: ${merge.impact})\n`;
    });
  } else {
    report += `Aucune suggestion de fusion pour le moment.\n`;
  }

  return report;
}

/**
 * Exporter l'analyse en JSON
 * @param {Object} analysis - Résultat d'analyse
 * @param {string} [filename] - Nom du fichier (optionnel)
 */
export function exportAnalysisToJSON(analysis, filename) {
  if (!analysis) return;

  const dataStr = JSON.stringify(analysis, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });

  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `analysis_${analysis.id || Date.now()}.json`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Exporter l'analyse en CSV
 * @param {Object} analysis - Résultat d'analyse
 * @param {string} [filename] - Nom du fichier (optionnel)
 */
export function exportAnalysisToCSV(analysis, filename) {
  if (!analysis || !Array.isArray(analysis.suggestedMerges)) return;

  let csv = 'Formation 1,Formation 2,Compétences partagées,Pourcentage,Raison,Impact\n';

  analysis.suggestedMerges.forEach(merge => {
    const formationIds = merge.formationIds || [];
    const sharedCompetences = merge.sharedCompetences || 0;
    const percent = merge.percentShared || 0;
    const reason = (merge.reason || '').replace(/,/g, ';');
    const impact = merge.impact || 'low';

    csv += `${formationIds[0] || ''},${formationIds[1] || ''},${sharedCompetences},${percent}%,${reason},${impact}\n`;
  });

  const dataBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `analysis_${analysis.id || Date.now()}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default {
  runFusionAnalysis,
  getAnalysis,
  listAnalyses,
  getSimilarityLevel,
  getSimilarityColor,
  getSimilarityLabel,
  formatTimeSaved,
  calculateFusionROI,
  sortSuggestionsByImpact,
  filterSuggestionsByImpact,
  generateAnalysisReport,
  exportAnalysisToJSON,
  exportAnalysisToCSV
};
